import { useState, type ReactNode } from 'react';
import {
  useCreateExtraordinaryExpense,
  useDeleteExtraordinaryExpense,
  useExtraordinaryExpenses,
} from '@/app/hooks';
import { useAuth } from '@/app/AuthContext';
import { useIsWideScreen } from '@/app/useIsWideScreen';
import { Card, SectionTitle } from '@/components/Card';
import { Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { Permission } from '@/types/permission';
import { NO_DATA, financialTone, formatAmd, toNumber } from '@/utils/format';
import { font, v, financialToneColor } from '@/theme/tokens';
import type { ExtraordinaryExpenseDetail } from '@/types/schemas';

interface Props {
  unitId: string;
  periodId: string;
}

const EMPTY_FORM = {
  expense_date: '',
  amount: '',
  expense_item: '',
  target: '',
  counterparty: '',
  comment: '',
};

/**
 * «Внеплановые расходы» — the accountant's breakdown of one Excel figure.
 *
 * The month's total is «Все расходы на др рестораны» from the confirmed
 * report and is never recomputed here: the rows below only explain what it
 * is made of, so the reconciliation shows Итого / Детализировано / Осталось
 * and warns when detail exceeds the total without ever changing it.
 */
export function ExtraordinaryExpenses({ unitId, periodId }: Props) {
  const query = useExtraordinaryExpenses(unitId, periodId);
  const create = useCreateExtraordinaryExpense();
  const remove = useDeleteExtraordinaryExpense();
  const { can } = useAuth();
  const wide = useIsWideScreen();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [error, setError] = useState<string | null>(null);

  const canEdit = can(Permission.EDIT_FINANCIAL_DATA);
  const data = query.data;
  if (!data) return null;

  const canonical = toNumber(data.canonical_total);
  const detailed = toNumber(data.detailed_total);
  const remaining = toNumber(data.remaining);

  const submit = () => {
    setError(null);
    if (!form.expense_date || !form.amount || !form.expense_item.trim() || !form.target.trim()) {
      setError('Заполните дату, сумму, статью расхода и объект.');
      return;
    }
    create.mutate(
      {
        unitId,
        input: {
          business_unit_id: unitId,
          financial_period_id: periodId,
          expense_date: form.expense_date,
          amount: form.amount,
          expense_item: form.expense_item.trim(),
          target: form.target.trim(),
          counterparty: form.counterparty.trim() || null,
          comment: form.comment.trim() || null,
        },
      },
      {
        onSuccess: () => {
          setForm(EMPTY_FORM);
          setOpen(false);
        },
        onError: (e) => setError(e instanceof Error ? e.message : 'Не удалось сохранить запись'),
      },
    );
  };

  return (
    <Card style={{ marginTop: 16 }}>
      <SectionTitle
        right={
          data.over_detailed ? (
            <Badge tone="warning">детализация превышает итог ОПУ</Badge>
          ) : undefined
        }
      >
        Внеплановые расходы
      </SectionTitle>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))',
          gap: 14,
          marginBottom: 14,
        }}
      >
        <Figure label="Итого по ОПУ" value={canonical} />
        <Figure label="Детализировано бухгалтером" value={detailed} />
        <Figure label="Осталось детализировать" value={remaining} tone />
      </div>

      {data.over_detailed && (
        <div
          style={{
            marginBottom: 14,
            padding: '10px 12px',
            borderRadius: 10,
            background: v('warning-soft'),
            color: v('warning'),
            fontSize: 12.5,
          }}
        >
          Сумма детализации больше итога по ОПУ. Итоговая сумма отчёта не меняется — проверьте
          введённые записи.
        </div>
      )}

      <div style={{ fontSize: 11.5, color: v('ink-faint'), marginBottom: 14 }}>
        Итог берётся из строки «Все расходы на др рестораны» подтверждённого отчёта. Ручные записи
        объясняют, из чего он состоит, и не увеличивают его.
      </div>

      {data.details.length === 0 && (
        <div style={{ fontSize: 13, color: v('ink-muted'), padding: '4px 0 12px' }}>
          Детализации пока нет.
        </div>
      )}

      {data.details.length > 0 &&
        (wide ? (
          <DetailTable rows={data.details} canEdit={canEdit} onDelete={(id) => remove.mutate(id)} />
        ) : (
          <DetailList rows={data.details} canEdit={canEdit} onDelete={(id) => remove.mutate(id)} />
        ))}

      {canEdit && !open && (
        <Button variant="primary" style={{ marginTop: 12 }} onClick={() => setOpen(true)}>
          Добавить запись
        </Button>
      )}

      {canEdit && open && (
        <div style={{ marginTop: 14, borderTop: `1px solid ${v('line')}`, paddingTop: 14 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: wide ? 'repeat(2, 1fr)' : '1fr',
              gap: 10,
            }}
          >
            <Field label="Дата" required>
              <input
                type="date"
                aria-label="Дата"
                value={form.expense_date}
                onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Сумма" required>
              <input
                type="number"
                step="0.01"
                aria-label="Сумма"
                value={form.amount}
                onChange={(e) => setForm({ ...form, amount: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Статья расхода" required>
              <input
                aria-label="Статья расхода"
                value={form.expense_item}
                onChange={(e) => setForm({ ...form, expense_item: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Куда относится" required>
              <input
                aria-label="Куда относится"
                value={form.target}
                onChange={(e) => setForm({ ...form, target: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Контрагент">
              <input
                aria-label="Контрагент"
                value={form.counterparty}
                onChange={(e) => setForm({ ...form, counterparty: e.target.value })}
                style={inputStyle}
              />
            </Field>
            <Field label="Комментарий">
              <input
                aria-label="Комментарий"
                value={form.comment}
                onChange={(e) => setForm({ ...form, comment: e.target.value })}
                style={inputStyle}
              />
            </Field>
          </div>

          {error && (
            <div style={{ color: v('negative'), fontSize: 12.5, marginTop: 10 }} role="alert">
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <Button variant="primary" onClick={submit} disabled={create.isPending}>
              {create.isPending ? 'Сохранение…' : 'Сохранить'}
            </Button>
            <Button
              onClick={() => {
                setOpen(false);
                setForm(EMPTY_FORM);
                setError(null);
              }}
            >
              Отмена
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 9,
  border: `1px solid ${v('line')}`,
  background: v('surface'),
  color: v('ink'),
  fontSize: 13,
} as const;

function Field({
  label,
  required = false,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 11.5, color: v('ink-muted'), marginBottom: 4 }}>
        {label}
        {required && <span style={{ color: v('negative') }}> *</span>}
      </span>
      {children}
    </label>
  );
}

/** `tone` marks a figure that follows the negative/positive rule — the
 * remaining amount going below zero is what over-detailing looks like. */
function Figure({ label, value, tone = false }: { label: string; value: number | null; tone?: boolean }) {
  const color = tone ? financialToneColor(financialTone(value, false)) : v('ink');
  return (
    <div>
      <div style={{ fontSize: 12, color: v('ink-muted'), marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, fontFamily: font.mono, color }}>
        {formatAmd(value)}
      </div>
    </div>
  );
}

function DetailTable({
  rows,
  canEdit,
  onDelete,
}: {
  rows: ExtraordinaryExpenseDetail[];
  canEdit: boolean;
  onDelete: (id: string) => void;
}) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead>
        <tr>
          {['Дата', 'Статья', 'Объект', 'Контрагент', 'Сумма', canEdit ? '' : null]
            .filter((header) => header !== null)
            .map((header, index) => (
              <th
                key={`${header}-${index}`}
                style={{
                  textAlign: index === 4 ? 'right' : 'left',
                  padding: '6px 8px',
                  fontSize: 11.5,
                  color: v('ink-muted'),
                  fontWeight: 600,
                  borderBottom: `1px solid ${v('line')}`,
                }}
              >
                {header as string}
              </th>
            ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td style={cellStyle}>{row.expense_date}</td>
            <td style={cellStyle}>
              {row.expense_item}
              {row.comment && (
                <div style={{ fontSize: 11.5, color: v('ink-muted') }}>{row.comment}</div>
              )}
            </td>
            <td style={cellStyle}>{row.target}</td>
            <td style={cellStyle}>{row.counterparty ?? NO_DATA}</td>
            <td style={{ ...cellStyle, textAlign: 'right', fontFamily: font.mono, fontWeight: 600 }}>
              {formatAmd(toNumber(row.amount))}
            </td>
            {canEdit && (
              <td style={{ ...cellStyle, textAlign: 'right' }}>
                <Button variant="danger" onClick={() => onDelete(row.id)} aria-label={`Удалить ${row.expense_item}`}>
                  Удалить
                </Button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const cellStyle = {
  padding: '8px 8px',
  borderBottom: `1px solid ${v('line-soft')}`,
} as const;

/** Phones get a compact list instead of a wide table. */
function DetailList({
  rows,
  canEdit,
  onDelete,
}: {
  rows: ExtraordinaryExpenseDetail[];
  canEdit: boolean;
  onDelete: (id: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((row) => (
        <div
          key={row.id}
          style={{ borderBottom: `1px solid ${v('line-soft')}`, paddingBottom: 10, fontSize: 13 }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <span style={{ fontWeight: 600 }}>{row.expense_item}</span>
            <span style={{ fontFamily: font.mono, fontWeight: 700 }}>
              {formatAmd(toNumber(row.amount))}
            </span>
          </div>
          <div style={{ fontSize: 11.5, color: v('ink-muted'), marginTop: 2 }}>
            {row.expense_date} · {row.target}
            {row.counterparty ? ` · ${row.counterparty}` : ''}
          </div>
          {canEdit && (
            <Button
              variant="danger"
              style={{ marginTop: 8, padding: '5px 10px', fontSize: 12 }}
              onClick={() => onDelete(row.id)}
              aria-label={`Удалить ${row.expense_item}`}
            >
              Удалить
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}
